import { useRef, useState, type FormEvent } from 'react';
import { Alert, Button, Card, Icon, Input, StatusPill, useToast } from './ui';
import { api, ApiError } from '../lib/api';
import { brl, cycleLabel, datePt, dateTimePt } from '../lib/format';
import { tr } from '../lib/i18n';

export type BillingCycle = 'monthly' | 'semiannual' | 'yearly';
export type PaymentMethod = 'card' | 'pix';

export interface PlanView {
  key: string;
  name: string;
  description: string | null;
  priceMonthCents: number;
  priceSemiannualCents: number;
  priceYearCents: number;
  annualDiscountPercent: number;
  maxProfessionals: number;
  features: { key: string; enabled: boolean }[];
}

export interface PixCharge {
  transactionId: string;
  reference: string;
  amountCents: number;
  createdAt: string;
  planName?: string;
  cycleLabel?: string;
  pixKey: string;
  payload: string;
  qrDataUrl: string;
}

export const FEATURE_LABEL: Record<string, string> = {
  online_booking: 'Reservas online',
  public_profile: 'Perfil público',
  agenda_daily: 'Agenda diária',
  agenda_weekly: 'Agenda semanal',
  agenda_monthly: 'Agenda mensal',
  customer_management: 'Gestão de clientes',
  history: 'Histórico',
  public_link: 'Link público',
  whatsapp_shortcut: 'Atalho WhatsApp',
  schedule_config: 'Configurar expediente',
  team: 'Equipe',
  individual_agendas: 'Agendas individuais',
  reports: 'Relatórios',
  blocked_periods: 'Períodos bloqueados',
  cancellation_history: 'Histórico de cancelamentos',
  report_export: 'Exportar relatórios',
  extended_gallery: 'Portfólio ampliado',
  permissions: 'Permissões',
  central_agenda: 'Agenda central',
  advanced_reports: 'Relatórios avançados',
  kpi: 'Indicadores (KPI)',
  services_per_professional: 'Serviços por profissional',
  business_settings: 'Configurações do negócio'
};

function featureText(key: string): string {
  const label = FEATURE_LABEL[key];
  return label ? tr(label) : key;
}

export function CycleSelector({ cycle, onChange }: { cycle: BillingCycle; onChange: (cycle: BillingCycle) => void }): JSX.Element {
  return (
    <div className="inline-flex rounded-full border border-line p-0.5" role="group" aria-label={tr("Ciclo de cobrança")}>
      {(['monthly', 'semiannual', 'yearly'] as const).map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          aria-pressed={cycle === option}
          className={`rounded-full px-3 py-1 text-sm font-semibold transition-all ${cycle === option ? 'bg-accent text-on-accent shadow-[0_8px_16px_-10px_var(--nl-accent)]' : 'text-muted hover:text-graphite'}`}
        >
          {cycleLabel(option)}
        </button>
      ))}
    </div>
  );
}

export function PaymentMethodSelector({
  method,
  methods,
  onChange
}: {
  method: PaymentMethod;
  methods: PaymentMethod[];
  onChange: (method: PaymentMethod) => void;
}): JSX.Element | null {
  if (methods.length <= 1) return null;
  const optionLabel: Record<PaymentMethod, string> = { card: tr('Cartão recorrente'), pix: tr('Pix à vista') };
  return (
    <div className="inline-flex rounded-full border border-line p-0.5" role="group" aria-label={tr("Forma de pagamento")}>
      {methods.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          aria-pressed={method === option}
          className={`rounded-full px-3 py-1 text-sm font-semibold transition-all ${method === option ? 'bg-accent text-on-accent shadow-[0_8px_16px_-10px_var(--nl-accent)]' : 'text-muted hover:text-graphite'}`}
        >
          {optionLabel[option]}
        </button>
      ))}
    </div>
  );
}

export function PlanCards({
  plans,
  cycle,
  method,
  checkoutMethods,
  busy,
  pixEnabled,
  currentPlanKey,
  canChoose,
  onChoose
}: {
  plans: PlanView[];
  cycle: BillingCycle;
  method: PaymentMethod;
  checkoutMethods: PaymentMethod[];
  busy: boolean;
  pixEnabled: boolean;
  currentPlanKey?: string;
  canChoose: boolean;
  onChoose: (planKey: string) => void;
}): JSX.Element {
  return (
    <div className="grid gap-4 md:grid-cols-3">
      {plans.map((p) => {
        const price = cycle === 'yearly' ? p.priceYearCents : cycle === 'semiannual' ? p.priceSemiannualCents : p.priceMonthCents;
        const isCurrent = currentPlanKey === p.key;
        return (
          <div key={p.key} className={`flex flex-col rounded-2xl border bg-card p-4 ${isCurrent ? 'border-accent shadow-md' : 'border-line'}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="font-semibold">{p.name}</p>
              {isCurrent ? <StatusPill tone="positive">{tr("Plano atual")}</StatusPill> : null}
            </div>
            <p className="mt-1 text-xs text-muted">{p.description ? tr(p.description) : p.description}</p>
            <p className="mt-3 text-2xl font-semibold tabular-nums">{brl(price)}</p>
            <p className="text-xs text-muted">
              {cycle === 'yearly'
                ? tr('por ano · {discount}% de desconto', { discount: p.annualDiscountPercent })
                : cycle === 'semiannual'
                  ? tr('por semestre')
                  : tr('por mês')}
            </p>
            <p className="mt-1 text-xs text-muted">{tr("Até")} {p.maxProfessionals} {tr("profissional(is)")}</p>
            <ul className="mt-3 flex-1 space-y-1 text-sm">
              {p.features.filter((f) => f.enabled).map((f) => (
                <li key={f.key} className="flex items-start gap-1.5">
                  <span className="mt-0.5 text-sage"><Icon name="check" size={15} /></span>
                  {featureText(f.key)}
                </li>
              ))}
            </ul>
            {canChoose ? (
              <div className="mt-4">
                <Button variant={isCurrent ? 'secondary' : 'primary'} className="w-full" disabled={busy || isCurrent} onClick={() => onChoose(p.key)}>
                  {isCurrent
                    ? tr('Plano em uso')
                    : checkoutMethods.length > 0
                      ? method === 'pix'
                        ? tr('Pagar com Pix')
                        : tr('Assinar no cartão')
                      : pixEnabled
                        ? tr('Pagar com Pix')
                        : tr('Solicitar plano')}
                </Button>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function LicenseActivationCard({
  businessId,
  onActivated
}: {
  businessId: string;
  onActivated: () => Promise<void> | void;
}): JSX.Element {
  const toast = useToast();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activated, setActivated] = useState<string | null>(null);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    const value = code.trim();
    if (!value) {
      setError(tr('Digite o código da licença.'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ license: { planName: string; currentPeriodEnd: string } }>('/api/licenses/redeem', {
        code: value,
        businessId
      });
      setActivated(tr('{plan} ativo até {date}.', { plan: res.license.planName, date: datePt(res.license.currentPeriodEnd) }));
      setCode('');
      toast(tr('Licença ativada. Acesso liberado.'), 'success');
      await onActivated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : tr('Não foi possível ativar a licença.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card as="section">
      <h2 className="mb-1 text-lg font-semibold">{tr("Já tem uma licença?")}</h2>
      <p className="mb-3 text-sm text-muted">
        {tr("Digite o código enviado pela administração da AgendaPro (formato NL-XXXX-XXXX-XXXX). A licença libera o programa pelo período contratado.")}
      </p>
      <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <label className="block flex-1">
          <span className="nl-label">{tr("Código de licença")}</span>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder={tr("NL-XXXX-XXXX-XXXX")}
            maxLength={20}
            autoComplete="off"
            spellCheck={false}
            className="font-mono tracking-wider"
          />
        </label>
        <Button type="submit" loading={busy} disabled={busy}>{tr("Ativar licença")}</Button>
      </form>
      {error ? <p className="mt-3 text-sm text-danger" role="alert">{error}</p> : null}
      {activated ? <div className="mt-3"><Alert tone="success" title={tr("Licença ativada")}>{activated}</Alert></div> : null}
    </Card>
  );
}

export function PixChargeCard({ charge, onDismiss }: { charge: PixCharge; onDismiss?: () => void }): JSX.Element {
  const toast = useToast();
  const payloadRef = useRef<HTMLTextAreaElement>(null);
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    const el = payloadRef.current;
    let copied = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(charge.payload);
        copied = true;
      }
    } catch {
      copied = false; // contexto sem foco: ainda dá para copiar pela seleção
    }
    if (!copied && el) {
      el.focus();
      el.select();
      copied = document.execCommand('copy');
      window.getSelection()?.removeAllRanges();
    }
    if (copied) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 4000);
      toast(tr('Código Pix copiado. Cole no app do seu banco.'), 'success');
    } else {
      toast(tr('Seu navegador bloqueou a cópia automática. Selecione o código acima e copie manualmente.'), 'info');
    }
  }

  return (
    <Card as="section">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{tr("Pague com Pix")}</h2>
          <p className="text-sm text-muted">
            {charge.planName ? `${charge.planName}` : tr('Cobrança do plano')}
            {charge.cycleLabel ? ` — ciclo ${charge.cycleLabel}` : ''} {tr("· gerada em")} {dateTimePt(charge.createdAt)}
          </p>
        </div>
        {onDismiss ? (
          <button type="button" onClick={onDismiss} className="rounded p-1 hover:bg-surface" aria-label={tr("Fechar cobrança Pix")}><Icon name="close" /></button>
        ) : null}
      </div>

      <div className="grid gap-5 md:grid-cols-[minmax(0,220px)_1fr]">
        <div className="flex flex-col items-center gap-2">
          <img
            src={charge.qrDataUrl}
            alt={tr('QR Code Pix de {amount} para a chave {key}', { amount: brl(charge.amountCents), key: charge.pixKey })}
            width={220}
            height={220}
            className="h-[220px] w-[220px] rounded-lg border border-line bg-white p-2"
          />
          <p className="text-sm font-semibold tabular-nums">{brl(charge.amountCents)}</p>
        </div>

        <ol className="nl-hint space-y-1.5">
          <li>{tr("1. Abra o app do seu banco e escolha")} <strong>{tr("Pix → pagar com QR Code")}</strong> {tr("ou")} <strong>{tr("Pix copia e cola")}</strong>.</li>
          <li>{tr("2. Confira se o valor exibido é")} <strong>{brl(charge.amountCents)}</strong> {tr("e se a chave de destino é")} <strong>{charge.pixKey}</strong>.</li>
          <li>{tr("3. Conclua o pagamento e")} <strong>{tr("guarde o comprovante")}</strong> {tr("com a referência")} <strong>{charge.reference}</strong>.</li>
          <li>{tr("4. Envie o comprovante pelo canal de suporte indicado no perfil da AgendaPro.")}</li>
        </ol>
      </div>

      <label className="mt-4 block">
        <span className="nl-label">{tr("Pix copia e cola")}</span>
        <textarea
          ref={payloadRef}
          readOnly
          value={charge.payload}
          rows={3}
          className="nl-input w-full break-all font-mono text-xs"
          aria-label={tr("Código Pix copia e cola")}
          onFocus={(e) => e.currentTarget.select()}
        />
      </label>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <Button variant="primary" onClick={() => void copy()}>{copied ? tr('Copiado') : tr('Copiar código Pix')}</Button>
        <span className="text-sm text-muted">{tr("Chave:")} <strong>{charge.pixKey}</strong> {tr("· Referência:")} <strong>{charge.reference}</strong></span>
      </div>

      <Alert tone="warning" title={tr("Como a licença é ativada")}>
        {tr("Este Pix cai direto na chave da plataforma: não há gateway que avise automaticamente quando você paga.")} <strong>{tr("O acesso continua bloqueado até a administração confirmar o recebimento")}</strong> {tr("e registrar a licença. Enviar a referência no comprovante é o que permite essa conferência.")}
      </Alert>
    </Card>
  );
}
